import { useState, useEffect } from "react";
import { Plus, Trash2, Loader2, FileText, Save, AlertTriangle, Info } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Alert, AlertDescription } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const DEPOSIT_OPTIONS = [
  { value: "25", label: "25%" },
  { value: "50", label: "50%" },
  { value: "100", label: "100% (Full Payment)" }
];

const QuoteBuilder = ({ 
  bookingId, 
  booking, 
  open, 
  onOpenChange, 
  onQuoteSent,
  existingQuote = null,
  onQuoteSaved = null // NEW: Callback when quote is saved (for immediate UI update)
}) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [stripeConnected, setStripeConnected] = useState(true);
  const [stripeMessage, setStripeMessage] = useState(null);
  
  // Track original quote state for dirty checking
  const [originalQuote, setOriginalQuote] = useState(null);
  // Track if we have a quote ID (to determine POST vs PUT)
  const [quoteId, setQuoteId] = useState(null);
  
  const [quote, setQuote] = useState({
    package_name: "",
    package_description: "",
    line_items: [{ description: "", quantity: 1, unit_price: 0 }],
    travel_fee: 0,
    travel_fee_notes: "",
    overtime_rate: null,
    overtime_notes: "",
    add_ons: [],
    deliverables: "",
    turnaround_days: 14,
    discount: 0,
    discount_notes: "",
    deposit_percentage: 50,
    valid_days: 3
  });
  
  // Estimated tax preview from backend (read-only)
  const [taxPreview, setTaxPreview] = useState({ 
    rate: 0, 
    label: null,
    estimated_amount: 0,
    available: false // Track if tax settings are configured
  });

  useEffect(() => {
    fetchTemplates();
    checkStripeStatus();
    fetchTaxSettings();
    if (existingQuote) {
      // Don't load tax_rate from existing quote - it's computed now
      const { tax_rate, quote_id, ...quoteWithoutTaxRate } = existingQuote;
      const loadedQuote = {
        ...quoteWithoutTaxRate,
        line_items: existingQuote.line_items?.length > 0 
          ? existingQuote.line_items 
          : [{ description: "", quantity: 1, unit_price: 0 }]
      };
      setQuote(loadedQuote);
      setOriginalQuote(JSON.stringify(loadedQuote)); // Store for dirty checking
      setQuoteId(quote_id || existingQuote.quote_id || null); // Store quote ID
    } else {
      setOriginalQuote(null);
      setQuoteId(null);
    }
  }, [existingQuote]);

  // Check if quote has been modified
  const isDirty = () => {
    if (!originalQuote) return true; // New quote is always "dirty"
    return JSON.stringify(quote) !== originalQuote;
  };

  const fetchTaxSettings = async () => {
    try {
      // Fetch photographer's tax settings (if configured)
      const response = await axios.get(`${API}/quotes/tax-settings`, { withCredentials: true });
      if (response.data) {
        const taxEnabled = response.data.tax_enabled && response.data.default_tax_rate > 0;
        setTaxPreview({
          rate: response.data.default_tax_rate || 0,
          label: response.data.tax_label || null,
          estimated_amount: 0,
          available: taxEnabled
        });
      }
    } catch (error) {
      // Tax settings not configured - that's OK, default to 0
      console.log("Tax settings not configured");
      setTaxPreview({ rate: 0, label: null, estimated_amount: 0, available: false });
    }
  };

  const checkStripeStatus = async () => {
    try {
      const response = await axios.get(`${API}/quotes/stripe-status`, { withCredentials: true });
      setStripeConnected(response.data.stripe_connected);
      setStripeMessage(response.data.message);
    } catch (error) {
      console.error("Failed to check Stripe status");
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/quotes/templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates");
    }
  };

  const loadTemplate = (templateId) => {
    const template = templates.find(t => t.template_id === templateId);
    if (template) {
      // Don't load tax_rate from template - it's computed now
      setQuote({
        ...quote,
        package_name: template.package_name,
        package_description: template.package_description || "",
        line_items: template.line_items?.length > 0 
          ? template.line_items 
          : [{ description: "", quantity: 1, unit_price: 0 }],
        travel_fee: template.travel_fee || 0,
        travel_fee_notes: template.travel_fee_notes || "",
        overtime_rate: template.overtime_rate,
        overtime_notes: template.overtime_notes || "",
        add_ons: template.add_ons || [],
        deliverables: template.deliverables || "",
        turnaround_days: template.turnaround_days || 14,
        discount: template.discount || 0,
        deposit_percentage: template.deposit_percentage || 50,
        valid_days: template.valid_days || 3
      });
      setSelectedTemplate(templateId);
      toast.success("Template loaded");
    }
  };

  const addLineItem = () => {
    setQuote({
      ...quote,
      line_items: [...quote.line_items, { description: "", quantity: 1, unit_price: 0 }]
    });
  };

  const removeLineItem = (index) => {
    if (quote.line_items.length > 1) {
      setQuote({
        ...quote,
        line_items: quote.line_items.filter((_, i) => i !== index)
      });
    }
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...quote.line_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setQuote({ ...quote, line_items: newItems });
  };

  const addAddOn = () => {
    setQuote({
      ...quote,
      add_ons: [...quote.add_ons, { description: "", quantity: 1, unit_price: 0 }]
    });
  };

  const removeAddOn = (index) => {
    setQuote({
      ...quote,
      add_ons: quote.add_ons.filter((_, i) => i !== index)
    });
  };

  const updateAddOn = (index, field, value) => {
    const newAddOns = [...quote.add_ons];
    newAddOns[index] = { ...newAddOns[index], [field]: value };
    setQuote({ ...quote, add_ons: newAddOns });
  };

  // Calculate totals - tax is now computed from photographer's tax settings
  const calculateTotals = () => {
    const itemsTotal = quote.line_items.reduce(
      (sum, item) => sum + (item.quantity || 0) * (parseFloat(item.unit_price) || 0), 0
    );
    const addOnsTotal = quote.add_ons.reduce(
      (sum, item) => sum + (item.quantity || 0) * (parseFloat(item.unit_price) || 0), 0
    );
    const subtotal = itemsTotal + addOnsTotal + (parseFloat(quote.travel_fee) || 0);
    const afterDiscount = subtotal - (parseFloat(quote.discount) || 0);
    
    // Tax is computed from photographer's configured rate (not user input)
    const taxRate = taxPreview.rate || 0;
    const taxAmount = afterDiscount * (taxRate / 100);
    const total = afterDiscount + taxAmount;
    const depositAmount = total * ((quote.deposit_percentage || 50) / 100);
    const balanceDue = total - depositAmount;

    return {
      subtotal: subtotal.toFixed(2),
      taxRate: taxRate,
      taxAmount: taxAmount.toFixed(2),
      total: total.toFixed(2),
      depositAmount: depositAmount.toFixed(2),
      balanceDue: balanceDue.toFixed(2)
    };
  };

  const totals = calculateTotals();

  const saveQuote = async (andSend = false) => {
    if (!quote.package_name.trim()) {
      toast.error("Please enter a package name");
      return;
    }
    if (!quote.deliverables.trim()) {
      toast.error("Please describe the deliverables");
      return;
    }

    // Check if there are changes to save (skip API call if no changes and not sending)
    if (!andSend && !isDirty()) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    if (andSend) setSending(true);

    try {
      // Determine if we should use POST (create) or PUT (update)
      // Use PUT if we have an existing quote (quoteId is set)
      const hasExistingQuote = quoteId || existingQuote;
      const method = hasExistingQuote ? "put" : "post";
      
      // Prepare quote data - exclude tax_rate (computed on backend)
      const { tax_rate, quote_id, ...quoteData } = quote;
      
      const response = await axios[method](
        `${API}/quotes/booking/${bookingId}`,
        {
          ...quoteData,
          line_items: quote.line_items.filter(i => i.description.trim()),
          add_ons: quote.add_ons.filter(i => i.description.trim()),
          template_id: selectedTemplate || undefined
        },
        { withCredentials: true }
      );

      // Store the new quote ID for future updates
      if (response.data?.quote_id) {
        setQuoteId(response.data.quote_id);
      }
      
      // Update original quote for dirty checking
      setOriginalQuote(JSON.stringify(quote));

      // Save as template if requested (also without tax_rate)
      if (saveAsTemplate && templateName.trim()) {
        await axios.post(
          `${API}/quotes/templates`,
          {
            name: templateName,
            ...quoteData,
            line_items: quote.line_items.filter(i => i.description.trim()),
            add_ons: quote.add_ons.filter(i => i.description.trim())
          },
          { withCredentials: true }
        );
        toast.success("Template saved!");
      }

      // Send quote if requested
      if (andSend) {
        await axios.post(
          `${API}/quotes/booking/${bookingId}/send`,
          {},
          { withCredentials: true }
        );
        toast.success("Quote sent to client!");
        onQuoteSent?.();
        onOpenChange(false);
      } else {
        toast.success(hasExistingQuote ? "Quote updated" : "Quote saved as draft");
        // Notify parent to refresh quote data WITHOUT closing the modal
        // Don't pass response.data as it only contains {quote_id, message}, not full quote
        onQuoteSaved?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save quote");
    } finally {
      setSaving(false);
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[90vh] flex flex-col p-0 overflow-hidden" data-testid="quote-builder-modal">
        {/* Fixed Header */}
        <DialogHeader className="p-6 pb-4 border-b shrink-0">
          <DialogTitle>
            {existingQuote || quoteId ? "Edit Quote" : "Build Quote"}
            {booking && ` for ${booking.customer_name}`}
            {existingQuote?.status === "draft" && (
              <Badge variant="secondary" className="ml-2">Draft</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6 pt-4">
          <div className="space-y-6">
          {/* Stripe Warning */}
          {!stripeConnected && (
            <Alert className="bg-yellow-500/10 border-yellow-500/30">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <AlertDescription className="text-yellow-200">
                <strong>Stripe account required</strong>
                <br />
                <span className="text-sm text-yellow-300/80">
                  {stripeMessage || "Set up your Stripe account in Settings → Payments to send quotes and accept payments."}
                </span>
                <br />
                <a 
                  href="/dashboard/payments" 
                  className="text-sm text-yellow-400 underline hover:text-yellow-300 mt-1 inline-block"
                >
                  Set Up Stripe →
                </a>
              </AlertDescription>
            </Alert>
          )}

          {/* Template Selection */}
          {!existingQuote && (
            <div>
              <Label>Load from Template</Label>
              {templates.length > 0 ? (
                <Select value={selectedTemplate} onValueChange={loadTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.template_id} value={t.template_id}>
                        {t.name} - {t.package_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-muted-foreground mt-1">
                  No templates yet. Save this quote as a template for future use.
                </p>
              )}
            </div>
          )}

          {/* Package Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="package_name">Package Name *</Label>
              <Input
                id="package_name"
                value={quote.package_name}
                onChange={(e) => setQuote({ ...quote, package_name: e.target.value })}
                placeholder="e.g., Wedding Photography Premium"
              />
            </div>
            <div>
              <Label htmlFor="turnaround">Turnaround (days)</Label>
              <Input
                id="turnaround"
                type="number"
                value={quote.turnaround_days}
                onChange={(e) => setQuote({ ...quote, turnaround_days: parseInt(e.target.value) || 14 })}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="package_desc">Package Description</Label>
            <Textarea
              id="package_desc"
              value={quote.package_description}
              onChange={(e) => setQuote({ ...quote, package_description: e.target.value })}
              placeholder="Brief description of the package..."
              rows={2}
            />
          </div>

          {/* Line Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Line Items</Label>
              <Button variant="outline" size="sm" onClick={addLineItem}>
                <Plus className="w-4 h-4 mr-1" /> Add Item
              </Button>
            </div>
            <div className="space-y-2">
              {quote.line_items.map((item, index) => (
                <div key={index} className="flex gap-2 items-center">
                  <Input
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateLineItem(index, "description", e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    type="number"
                    placeholder="Qty"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, "quantity", parseInt(e.target.value) || 1)}
                    className="w-20"
                  />
                  <Input
                    type="number"
                    placeholder="Price"
                    value={item.unit_price}
                    onChange={(e) => updateLineItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                    className="w-28"
                  />
                  <span className="w-24 text-right">
                    ${((item.quantity || 0) * (parseFloat(item.unit_price) || 0)).toFixed(2)}
                  </span>
                  {quote.line_items.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => removeLineItem(index)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Travel & Overtime */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="travel_fee">Travel Fee ($)</Label>
              <Input
                id="travel_fee"
                type="number"
                value={quote.travel_fee}
                onChange={(e) => setQuote({ ...quote, travel_fee: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label htmlFor="overtime_rate">Overtime Rate ($/hour)</Label>
              <Input
                id="overtime_rate"
                type="number"
                value={quote.overtime_rate || ""}
                onChange={(e) => setQuote({ ...quote, overtime_rate: parseFloat(e.target.value) || null })}
                placeholder="Optional"
              />
            </div>
          </div>

          {/* Add-ons */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Add-ons (Optional)</Label>
              <Button variant="outline" size="sm" onClick={addAddOn}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            {quote.add_ons.length > 0 && (
              <div className="space-y-2">
                {quote.add_ons.map((item, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateAddOn(index, "description", e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateAddOn(index, "quantity", parseInt(e.target.value) || 1)}
                      className="w-20"
                    />
                    <Input
                      type="number"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => updateAddOn(index, "unit_price", parseFloat(e.target.value) || 0)}
                      className="w-28"
                    />
                    <Button variant="ghost" size="icon" onClick={() => removeAddOn(index)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Deliverables */}
          <div>
            <Label htmlFor="deliverables">Deliverables *</Label>
            <Textarea
              id="deliverables"
              value={quote.deliverables}
              onChange={(e) => setQuote({ ...quote, deliverables: e.target.value })}
              placeholder="e.g., 60 edited high-resolution photos, full gallery of proofs, online gallery access"
              rows={2}
            />
          </div>

          {/* Discount & Quote Valid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="discount">Discount ($)</Label>
              <Input
                id="discount"
                type="number"
                value={quote.discount}
                onChange={(e) => setQuote({ ...quote, discount: parseFloat(e.target.value) || 0 })}
                data-testid="quote-discount-input"
              />
            </div>
            <div>
              <Label htmlFor="valid_days">Quote Valid (days)</Label>
              <Input
                id="valid_days"
                type="number"
                value={quote.valid_days}
                onChange={(e) => setQuote({ ...quote, valid_days: parseInt(e.target.value) || 3 })}
                data-testid="quote-valid-days-input"
              />
            </div>
          </div>

          {/* Payment Terms */}
          <div>
            <Label>Deposit Required</Label>
            <Select 
              value={String(quote.deposit_percentage)} 
              onValueChange={(v) => setQuote({ ...quote, deposit_percentage: parseInt(v) })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEPOSIT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Summary */}
          <div className="bg-muted/50 rounded-lg p-4" data-testid="quote-summary">
            <h4 className="font-semibold mb-3">Quote Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>${totals.subtotal}</span>
              </div>
              {parseFloat(quote.discount) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-${parseFloat(quote.discount).toFixed(2)}</span>
                </div>
              )}
              {taxPreview.available && totals.taxRate > 0 ? (
                <div className="flex justify-between text-muted-foreground">
                  <span className="flex items-center gap-1">
                    {taxPreview.label || "Tax"} ({totals.taxRate}%)
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">Estimated until quote is finalized</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                  <span>${totals.taxAmount}</span>
                </div>
              ) : null}
              <div className="flex justify-between font-semibold text-lg border-t pt-2">
                <span>Total</span>
                <span>${totals.total}</span>
              </div>
              {/* Tax disclaimer when estimate is not available */}
              {!taxPreview.available && (
                <p className="text-xs text-muted-foreground italic">
                  * Total does not include tax
                </p>
              )}
              <div className="flex justify-between text-muted-foreground">
                <span>Deposit ({quote.deposit_percentage}%)</span>
                <span>${totals.depositAmount}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Balance Due</span>
                <span>${totals.balanceDue}</span>
              </div>
            </div>
          </div>

          {/* Save as Template */}
          {!existingQuote && !quoteId && (
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="saveTemplate"
                checked={saveAsTemplate}
                onChange={(e) => setSaveAsTemplate(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="saveTemplate" className="flex-1 cursor-pointer">
                Save as template for future use
              </Label>
              {saveAsTemplate && (
                <Input
                  placeholder="Template name..."
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-48"
                />
              )}
            </div>
          )}
          </div>
        </div>

        {/* Fixed Footer */}
        <DialogFooter className="gap-2 p-6 pt-4 border-t shrink-0 bg-card">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => saveQuote(false)} 
            disabled={saving || !stripeConnected}
            title={!stripeConnected ? "Stripe account required" : ""}
            data-testid="quote-save-draft-btn"
          >
            {saving && !sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Draft
          </Button>
          <Button 
            onClick={() => saveQuote(true)} 
            disabled={saving || !stripeConnected}
            title={!stripeConnected ? "Stripe account required" : ""}
            data-testid="quote-send-btn"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
            Send Quote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteBuilder;
